import React, {useCallback} from 'react';
import {DistributionMarketer, DistributionProduct, AvailableSerial, CreateDeliveryForm} from '../models/distribution.model';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import {useMarketerDelivery} from '../hooks/useMarketerDelivery';
import DeliveryFormHeader from './DeliveryFormHeader';
import DataLoadingStatus from './DataLoadingStatus';
import DeliveryFormFields from './DeliveryFormFields';
import QuantityField from './QuantityField';
import SerialSelectionSections from './SerialSelectionSections';
import DeliveryFormActions from './DeliveryFormActions';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

interface MarketerDeliveryTabProps {
  marketers: DistributionMarketer[];
  products: DistributionProduct[];
  availableSerials: AvailableSerial[];
  error: string | null;
  isLoading: boolean;
  onSubmit: (formData: CreateDeliveryForm) => Promise<boolean>;
  onFetchMarketers: (search?: string) => void;
  onFetchProducts: (search?: string) => void;
  onFetchAvailableSerials: (productId: string) => Promise<void>;
  onClearAvailableSerials?: () => void;
}

const MarketerDeliveryTab: React.FC<MarketerDeliveryTabProps> = ({
  marketers,
  products,
  availableSerials,
  error,
  isLoading,
  onSubmit,
  onFetchMarketers,
  onFetchProducts,
  onFetchAvailableSerials,
  onClearAvailableSerials
}) => {
  const {hasPermission} = useAuthorization();

  const {
    isSearchingMarketers, isSearchingProducts,
    setIsSearchingMarketers, setIsSearchingProducts,
    deliveryFormData: formData,
    isSubmittingDelivery: submitting,
    selectedSerials,
    bulkSerials,
    setBulkSerials,
    handleInputChange,
    handleSerialToggle,
    handleSubmit,
    resetForm,
    getSelectedProduct,
    getSelectedMarketer,
    handleSelectAll,
    handleClearSelection
  } = useMarketerDelivery({
    products,
    onSubmit,
    onFetchAvailableSerials,
    onClearAvailableSerials
  });

  const selectedProduct = getSelectedProduct();
  const selectedMarketer = getSelectedMarketer(marketers);

  const onSelectAll = () => handleSelectAll(availableSerials);
  const onClearSelection = () => handleClearSelection();

  // Enhanced search handlers with loading states
  const handleSearchMarketers = useCallback(async (searchTerm: string) => {
    if (searchTerm.trim()) {
      setIsSearchingMarketers(true);
      try {
        await onFetchMarketers(searchTerm);
      } finally {
        setIsSearchingMarketers(false);
      }
    } else {
      onFetchMarketers();
    }
  }, [onFetchMarketers]);

  const handleSearchProducts = useCallback(async (searchTerm: string) => {
    if (searchTerm.trim()) {
      setIsSearchingProducts(true);
      try {
        await onFetchProducts(searchTerm);
      } finally {
        setIsSearchingProducts(false);
      }
    } else {
      onFetchProducts();
    }
  }, [onFetchProducts]);

  // Check if user has permission to create distribution
  if (!hasPermission(PERMISSIONS.CREATE_DISTRIBUTION)) {
    return (
      <PermissionDeniedCard
        title="غير مصرح لك"
        message="ليس لديك صلاحية لإنشاء توزيع جديد"
      />
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <Card className="p-4 flex-1 flex flex-col">
        <DeliveryFormHeader />
        <DataLoadingStatus
          marketersCount={marketers.length}
          productsCount={products.length}
          onFetchMarketers={() => onFetchMarketers()}
          onFetchProducts={() => onFetchProducts()}
        />

        {/* Error is handled by StandardizedErrorHandler and shown as toast notification */}

        <form onSubmit={handleSubmit} className="space-y-4 flex-1 flex flex-col">
          <DeliveryFormFields
            formData={formData}
            marketers={marketers}
            products={products}
            selectedMarketer={selectedMarketer}
            selectedProduct={selectedProduct}
            onInputChange={handleInputChange}
            onSearchMarketers={handleSearchMarketers}
            onSearchProducts={handleSearchProducts}
            isLoadingMarketers={isSearchingMarketers}
            isLoadingProducts={isSearchingProducts}
          />

          <QuantityField
            formData={formData}
            selectedProduct={selectedProduct}
            onInputChange={handleInputChange}
          />

          <SerialSelectionSections
            selectedProduct={selectedProduct}
            availableSerials={availableSerials}
            selectedSerials={selectedSerials}
            bulkSerials={bulkSerials}
            isLoading={isLoading}
            onSerialToggle={handleSerialToggle}
            onBulkSerialsChange={setBulkSerials}
            onSelectAll={onSelectAll}
            onClearSelection={onClearSelection}
          />

          <DeliveryFormActions
            formData={formData}
            submitting={submitting}
            isLoading={isLoading}
            onReset={resetForm}
          />
        </form>
      </Card>
    </div>
  );
};

export default MarketerDeliveryTab;
